import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Search, Filter, Loader2, Heart, Users, Calendar, ArrowLeft, X } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import apiClient from '../services/api';

const SearchResultsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const query = searchParams.get('q') || '';
  const category = searchParams.get('category') || 'all';
  
  const [searchQuery, setSearchQuery] = useState(query);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(category);

  const categories = [
    { value: 'all', label: 'All Categories' },
    { value: 'medical', label: 'Medical' },
    { value: 'education', label: 'Education' },
    { value: 'emergency', label: 'Emergency' },
    { value: 'community', label: 'Community' },
    { value: 'animals', label: 'Animals' },
    { value: 'environment', label: 'Environment' },
    { value: 'creative', label: 'Creative' },
    { value: 'business', label: 'Business' },
    { value: 'sports', label: 'Sports' },
    { value: 'other', label: 'Other' },
  ];

  useEffect(() => {
    const fetchResults = async () => {
      if (!query) {
        setCampaigns([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const params = new URLSearchParams();
        params.append('search', query);
        if (selectedCategory && selectedCategory !== 'all') {
          params.append('category', selectedCategory);
        }
        params.append('limit', '50');
        params.append('exclude_deleted', 'true');
        
        const response = await apiClient.get(`/campaigns?${params.toString()}`);
        const campaignList = Array.isArray(response.data) ? response.data : response.data.campaigns || [];
        setCampaigns(campaignList.filter(c => c.status === 'active'));
      } catch (error) {
        console.error('Search error:', error);
        setCampaigns([]);
      } finally {
        setLoading(false);
      }
    };

    fetchResults();
  }, [query, selectedCategory]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      setSearchParams({ q: searchQuery.trim(), category: selectedCategory });
    }
  };

  const handleCategoryChange = (value) => {
    setSelectedCategory(value);
    if (query) {
      setSearchParams({ q: query, category: value });
    }
  };

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const calculateProgress = (raised, goal) => {
    if (!goal || goal === 0) return 0;
    return Math.min((raised / goal) * 100, 100);
  };

  const handleImageError = (e) => {
    e.target.src = 'https://placehold.co/400x300/1a1a2e/4ade80?text=Campaign';
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back button */}
        <Button 
          variant="ghost" 
          className="mb-4" 
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        {/* Search Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-4">
            Search Results
          </h1>
          
          {/* Search Form */}
          <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search campaigns..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 h-12"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            
            <Select value={selectedCategory} onValueChange={handleCategoryChange}>
              <SelectTrigger className="w-full sm:w-48 h-12">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((cat) => (
                  <SelectItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Button type="submit" className="h-12 px-8 bg-emerald-600 hover:bg-emerald-700">
              Search
            </Button>
          </form>
        </div>

        {/* Results Count */}
        {query && !loading && (
          <p className="text-muted-foreground mb-6">
            {campaigns.length} {campaigns.length === 1 ? 'result' : 'results'} for "{query}"
            {selectedCategory !== 'all' && ` in ${categories.find(c => c.value === selectedCategory)?.label}`}
          </p>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
            <span className="ml-3 text-muted-foreground">Searching campaigns...</span>
          </div>
        )}

        {/* No Query */}
        {!query && !loading && (
          <div className="text-center py-20">
            <Search className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-foreground mb-2">Enter a search term</h2>
            <p className="text-muted-foreground">Search for campaigns by name, category, or cause</p>
          </div>
        )}

        {/* No Results */}
        {query && !loading && campaigns.length === 0 && (
          <div className="text-center py-20">
            <Search className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-foreground mb-2">No campaigns found</h2>
            <p className="text-muted-foreground mb-4">
              Try different keywords or browse all campaigns
            </p>
            <Button 
              onClick={() => navigate('/discover?all=true')}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              Browse All Campaigns
            </Button>
          </div>
        )}

        {/* Results Grid */}
        {!loading && campaigns.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {campaigns.map((campaign) => (
              <Link
                key={campaign.id}
                to={`/campaign/${campaign.id}`}
                className="group"
              >
                <Card className="h-full overflow-hidden hover:shadow-lg transition-shadow border-border hover:border-emerald-500/30">
                  {/* Campaign Image */}
                  <div className="relative aspect-video overflow-hidden">
                    <img
                      src={campaign.image || 'https://placehold.co/400x300/1a1a2e/4ade80?text=Campaign'}
                      alt={campaign.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      onError={handleImageError}
                    />
                    {campaign.category && (
                      <Badge className="absolute top-3 left-3 bg-emerald-600/90">
                        {campaign.category}
                      </Badge>
                    )}
                  </div>
                  
                  <CardContent className="p-4">
                    <h3 className="font-semibold text-foreground mb-2 line-clamp-2 group-hover:text-emerald-500 transition-colors">
                      {campaign.title}
                    </h3>
                    
                    {/* Progress Bar */}
                    <div className="mb-3">
                      <div className="h-2 bg-secondary rounded-full overflow-hidden">
                        <div
                          className="h-full bg-emerald-500 transition-all duration-300"
                          style={{ width: `${calculateProgress(campaign.raised, campaign.goal)}%` }}
                        />
                      </div>
                    </div>
                    
                    {/* Stats */}
                    <div className="flex items-center justify-between text-sm">
                      <div>
                        <span className="font-semibold text-emerald-500">
                          {formatCurrency(campaign.raised, campaign.currency)}
                        </span>
                        <span className="text-muted-foreground"> raised</span>
                      </div>
                      <span className="text-muted-foreground">
                        {Math.round(calculateProgress(campaign.raised, campaign.goal))}%
                      </span>
                    </div>
                    
                    {/* Goal */}
                    <p className="text-xs text-muted-foreground mt-1">
                      Goal: {formatCurrency(campaign.goal, campaign.currency)}
                    </p>
                    
                    {/* Meta Info */}
                    <div className="flex items-center gap-3 mt-3 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Users className="w-3 h-3" />
                        {campaign.donors || 0} donors
                      </span>
                      {campaign.days_left > 0 && (
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {campaign.days_left} days left
                        </span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchResultsPage;
